declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

interface GTagEvent {
  action: string;
  category: string;
  label?: string;
  value?: number;
}

class Analytics {
  private measurementId: string;

  constructor(measurementId: string) {
    this.measurementId = measurementId;
  }

  init(): void {
    if (typeof window === 'undefined') return;

    // Load the GA4 script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;
    document.head.appendChild(script);

    // Initialize dataLayer and gtag function
    window.dataLayer = window.dataLayer || [];
    function gtag(...args: unknown[]) {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;
    gtag('js', new Date());
    gtag('config', this.measurementId, {
      send_page_view: false, // Handle manually for SPA
    });
  }

  trackPageView(path: string, title?: string): void {
    if (typeof window === 'undefined' || !window.gtag) return;

    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title,
    });
  }

  trackEvent(event: GTagEvent): void {
    if (typeof window === 'undefined' || !window.gtag) return;

    window.gtag('event', event.action, {
      event_category: event.category,
      event_label: event.label,
      value: event.value,
    });
  }

  // Spot-related events
  trackSpotClaimed(spotId: string, buildingId: string): void {
    this.trackEvent({
      action: 'spot_claimed',
      category: 'spots',
      label: spotId,
      value: undefined,
    });
  }

  trackSessionEnded(durationMinutes: number): void {
    this.trackEvent({
      action: 'session_ended',
      category: 'sessions',
      value: durationMinutes,
    });
    // Also fire as a GA4 timing event (ms) for the standard timing reports
    this.trackTiming('session_duration', durationMinutes * 60_000, 'sessions');
  }

  trackSpotMarkedFree(spotId: string, durationMinutes?: number): void {
    this.trackEvent({
      action: 'spot_marked_free',
      category: 'spots',
      label: spotId,
      value: durationMinutes,
    });
    if (durationMinutes !== undefined) {
      this.trackTiming('mark_free_duration', durationMinutes * 60_000, 'spots');
    }
  }

  trackScheduleCreated(recurringDays: number): void {
    this.trackEvent({
      action: 'schedule_created',
      category: 'scheduling',
      value: recurringDays,
    });
  }

  trackReclaimAlertSent(spotId: string): void {
    this.trackEvent({
      action: 'reclaim_alert_sent',
      category: 'reclaim',
      label: spotId,
    });
  }

  trackNotificationMasterToggled(enabled: boolean): void {
    this.trackEvent({
      action: 'notification_master_toggled',
      category: 'notifications',
      label: enabled ? 'on' : 'off',
    });
  }

  trackNotificationTopicToggled(topic: string, enabled: boolean): void {
    this.trackEvent({
      action: 'notification_topic_toggled',
      category: 'notifications',
      label: `${topic}:${enabled ? 'on' : 'off'}`,
    });
  }

  trackError(errorCode: string, errorMessage?: string): void {
    this.trackEvent({
      action: 'error',
      category: 'errors',
      label: errorCode,
    });
    console.error(`[GA] Error tracked: ${errorCode}`, errorMessage);
  }

  trackOfferMade(spotId: string, requesterApartment?: string): void {
    this.trackEvent({
      action: 'offer_made',
      category: 'spot_requests',
      label: requesterApartment || spotId,
    });
  }

  trackRequestIgnored(requesterApartment?: string): void {
    this.trackEvent({
      action: 'request_ignored',
      category: 'spot_requests',
      label: requesterApartment,
    });
  }

  // Password reset events
  trackPasswordResetRequested(): void {
    this.trackEvent({
      action: 'password_reset_requested',
      category: 'auth',
    });
  }

  trackPasswordResetCompleted(): void {
    this.trackEvent({
      action: 'password_reset_completed',
      category: 'auth',
    });
  }

  trackPasswordResetFailed(errorType: string): void {
    this.trackEvent({
      action: 'password_reset_failed',
      category: 'auth',
      label: errorType,
    });
  }

  // Authentication events
  trackUserRegistered(registrationPath: 'admin_enlisted' | 'self_registered' = 'admin_enlisted'): void {
    this.trackEvent({
      action: 'user_registered',
      category: 'auth',
      label: registrationPath,
    });
  }

  trackUserLoggedIn(): void {
    this.trackEvent({
      action: 'user_logged_in',
      category: 'auth',
    });
  }

  trackUserLoggedOut(): void {
    this.trackEvent({
      action: 'user_logged_out',
      category: 'auth',
    });
  }

  trackUserLoginFailed(errorCode: string): void {
    this.trackEvent({
      action: 'user_login_failed',
      category: 'auth',
      label: errorCode,
    });
  }

  // Search/filter events
  trackSearchFiltered(filterLevel?: string): void {
    this.trackEvent({
      action: 'search_filtered',
      category: 'search',
      label: filterLevel,
    });
  }

  trackAttributeFilterApplied(activeCount: number): void {
    this.trackEvent({
      action: 'attribute_filter_applied',
      category: 'search',
      value: activeCount,
    });
  }

  // Spot management events
  trackSpotRegistered(levelCode: string): void {
    this.trackEvent({
      action: 'spot_registered',
      category: 'spots',
      label: levelCode,
    });
  }

  trackSpotDeleted(spotId: string): void {
    this.trackEvent({
      action: 'spot_deleted',
      category: 'spots',
      label: spotId,
    });
  }

  // Availability window events
  trackAvailabilityWindowCreated(type: 'free' | 'block', durationMinutes?: number): void {
    this.trackEvent({
      action: `window_created_${type}`,
      category: 'availability',
      value: durationMinutes,
    });
    if (durationMinutes !== undefined) {
      this.trackTiming(`window_${type}_duration`, durationMinutes * 60_000, 'availability');
    }
  }

  trackAvailabilityWindowClosed(type: 'free' | 'block'): void {
    this.trackEvent({
      action: `window_closed_${type}`,
      category: 'availability',
    });
  }

  // Owner edited a future window's range (Edit modal on SpotDetailPage).
  trackAvailabilityWindowEdited(windowId: string): void {
    this.trackEvent({
      action: 'window_edited',
      category: 'availability',
      label: windowId,
    });
  }

  // ─── Future-claim negotiation flow ───────────────────────────────────────
  //
  // Owner ↔ scheduled-claimer "change request" lifecycle. These power the
  // adoption + funnel metrics for the negotiation feature.

  trackFutureSpotClaimed(spotId: string, levelCode: string): void {
    this.trackEvent({
      action: 'future_spot_claimed',
      category: 'sessions',
      label: `${levelCode}|${spotId}`,
    });
  }

  trackAvailabilityChangeRequested(spotId: string): void {
    this.trackEvent({
      action: 'availability_change_requested',
      category: 'availability_change',
      label: spotId,
    });
  }

  trackAvailabilityChangeAccepted(spotId: string): void {
    this.trackEvent({
      action: 'availability_change_accepted',
      category: 'availability_change',
      label: spotId,
    });
  }

  trackAvailabilityChangeClaimCancelled(spotId: string): void {
    this.trackEvent({
      action: 'availability_change_claim_cancelled',
      category: 'availability_change',
      label: spotId,
    });
  }

  trackAvailabilityChangeDismissed(spotId: string): void {
    this.trackEvent({
      action: 'availability_change_dismissed',
      category: 'availability_change',
      label: spotId,
    });
  }

  // Schedule events
  trackScheduleDeleted(): void {
    this.trackEvent({
      action: 'schedule_deleted',
      category: 'scheduling',
    });
  }

  // Session extension events
  trackSessionExtensionRequested(minutesRequested: number): void {
    this.trackEvent({
      action: 'session_extension_requested',
      category: 'sessions',
      value: minutesRequested,
    });
    this.trackTiming('extension_requested_minutes', minutesRequested * 60_000, 'sessions');
  }

  trackSessionExtensionApproved(minutesGranted: number): void {
    this.trackEvent({
      action: 'session_extension_approved',
      category: 'sessions',
      value: minutesGranted,
    });
    this.trackTiming('extension_granted_minutes', minutesGranted * 60_000, 'sessions');
  }

  // Reclaim alert events
  trackReclaimAlertAcknowledged(spotId: string): void {
    this.trackEvent({
      action: 'reclaim_alert_acknowledged',
      category: 'reclaim',
      label: spotId,
    });
  }

  trackReclaimAlertIgnored(spotId: string): void {
    this.trackEvent({
      action: 'reclaim_alert_ignored',
      category: 'reclaim',
      label: spotId,
    });
  }

  // Spot discovery events
  // Note: trackReclaimAlertDismissed removed — no such UI action exists.
  // Alerts are either acknowledged (trackReclaimAlertAcknowledged) or
  // ignored (trackReclaimAlertIgnored when session ends without ack).
  trackSpotDetailsViewed(spotId: string, level?: string): void {
    this.trackEvent({
      action: 'spot_details_viewed',
      category: 'discovery',
      label: level,
    });
  }

  // Admin actions
  trackInviteCodeGenerated(): void {
    this.trackEvent({
      action: 'invite_code_generated',
      category: 'admin',
    });
  }

  trackBuildingLevelCreated(levelCode: string): void {
    this.trackEvent({
      action: 'building_level_created',
      category: 'admin',
      label: levelCode,
    });
  }

  trackBuildingLevelDeleted(levelCode: string): void {
    this.trackEvent({
      action: 'building_level_deleted',
      category: 'admin',
      label: levelCode,
    });
  }

  // Notification engagement
  trackReclaimAlertViewed(spotId: string): void {
    this.trackEvent({
      action: 'reclaim_alert_viewed',
      category: 'notifications',
      label: spotId,
    });
  }

  // Session timing events
  trackSessionStarted(sessionId: string): void {
    this.trackEvent({
      action: 'session_started_tracked',
      category: 'sessions',
      label: sessionId,
    });
  }

  // Note: trackSessionTiming removed — duplicates trackSessionEnded
  // which already tracks duration. GA4 also auto-tracks engagement time.

  // Page engagement — called from usePageTracking hook on route change
  trackPageEngagement(pagePath: string, timeSpentSeconds: number): void {
    this.trackEvent({
      action: 'page_engagement',
      category: 'engagement',
      label: pagePath,
      value: timeSpentSeconds,
    });
    this.trackTiming('page_time_on_page', timeSpentSeconds * 1000, 'engagement', pagePath);
  }

  trackScrollDepth(pagePath: string, depth: number): void {
    this.trackEvent({
      action: 'scroll_depth',
      category: 'engagement',
      label: pagePath,
      value: depth,
    });
  }

  // PWA events
  trackAppInstalled(): void {
    this.trackEvent({
      action: 'app_installed',
      category: 'pwa',
    });
  }

  // Offline/connectivity events
  trackOfflineUsage(): void {
    this.trackEvent({
      action: 'offline_usage',
      category: 'connectivity',
    });
  }

  trackOfflineToOnlineSync(): void {
    this.trackEvent({
      action: 'offline_to_online_sync',
      category: 'connectivity',
    });
  }

  // Call feature events - tracking user IDs without exposing phone numbers
  trackOccupantCallInitiated(ownerId: string, occupantId: string): void {
    this.trackEvent({
      action: 'occupant_call_initiated',
      category: 'communication',
      label: `owner:${ownerId}|occupant:${occupantId}`,
    });
  }

  trackOccupantWhatsAppInitiated(ownerId: string, occupantId: string): void {
    this.trackEvent({
      action: 'occupant_whatsapp_initiated',
      category: 'communication',
      label: `owner:${ownerId}|occupant:${occupantId}`,
    });
  }

  // ─── Lead capture (join building) ───────────────────────────────────────

  trackJoinBuildingPageViewed(): void {
    this.trackEvent({ action: 'join_building_page_viewed', category: 'leads' });
  }

  trackJoinBuildingSubmitted(role: string): void {
    this.trackEvent({ action: 'join_building_submitted', category: 'leads', label: role });
  }

  trackJoinBuildingSuccess(role: string): void {
    this.trackEvent({ action: 'join_building_success', category: 'leads', label: role });
  }

  trackJoinBuildingFailed(role: string): void {
    this.trackEvent({ action: 'join_building_failed', category: 'leads', label: role });
  }

  // ─── P2: Funnel start events ───────────────────────────────────────────

  trackRegisterStarted(): void {
    this.trackEvent({ action: 'register_started', category: 'auth' });
  }

  trackLoginStarted(): void {
    this.trackEvent({ action: 'login_started', category: 'auth' });
  }

  trackInviteCodeValidated(): void {
    this.trackEvent({ action: 'invite_code_validated', category: 'auth' });
  }

  trackSelfRegFormShown(): void {
    this.trackEvent({ action: 'self_register_form_shown', category: 'auth' });
  }

  trackInviteCodeInvalid(errorCode?: string): void {
    this.trackEvent({
      action: 'invite_code_invalid',
      category: 'auth',
      label: errorCode,
    });
  }

  trackClaimInitiated(spotId: string, level?: string): void {
    this.trackEvent({
      action: 'claim_initiated',
      category: 'spots',
      label: level || spotId,
    });
  }

  trackMarkFreeStarted(spotId: string): void {
    this.trackEvent({
      action: 'mark_free_started',
      category: 'spots',
      label: spotId,
    });
  }

  // ─── P2: Push notification events ──────────────────────────────────────

  trackPushPermission(state: 'granted' | 'denied' | 'default'): void {
    this.trackEvent({
      action: `push_permission_${state}`,
      category: 'notifications',
    });
  }

  trackPushTokenRegistered(): void {
    this.trackEvent({
      action: 'push_token_registered',
      category: 'notifications',
    });
  }

  trackPushTokenDisabled(): void {
    this.trackEvent({
      action: 'push_token_disabled',
      category: 'notifications',
    });
  }

  trackNotificationSettingsChanged(enabled: boolean): void {
    this.trackEvent({
      action: 'notification_settings_changed',
      category: 'notifications',
      label: enabled ? 'enabled' : 'disabled',
    });
  }

  // ─── P2: Spot request / offer events ───────────────────────────────────

  trackSpotRequestCreated(): void {
    this.trackEvent({ action: 'spot_request_created', category: 'spot_requests' });
  }

  trackSpotRequestCancelled(): void {
    this.trackEvent({ action: 'spot_request_cancelled', category: 'spot_requests' });
  }

  trackSpotRequestExpired(): void {
    this.trackEvent({ action: 'spot_request_expired', category: 'spot_requests' });
  }

  trackSpotRequestScheduleCreated(): void {
    this.trackEvent({ action: 'spot_request_schedule_created', category: 'spot_requests' });
  }

  trackSpotRequestScheduleToggled(isActive: boolean): void {
    this.trackEvent({
      action: isActive ? 'spot_request_schedule_resumed' : 'spot_request_schedule_paused',
      category: 'spot_requests',
    });
  }

  trackSpotRequestScheduleDeleted(): void {
    this.trackEvent({ action: 'spot_request_schedule_deleted', category: 'spot_requests' });
  }

  trackSpotOfferAccepted(spotId: string): void {
    this.trackEvent({
      action: 'spot_offer_accepted',
      category: 'spot_requests',
      label: spotId,
    });
  }

  trackSpotOfferRejected(spotId: string): void {
    this.trackEvent({
      action: 'spot_offer_rejected',
      category: 'spot_requests',
      label: spotId,
    });
  }

  // ─── P2: Session lifecycle (additional) ────────────────────────────────

  trackSessionExtensionRejected(reason?: string): void {
    this.trackEvent({
      action: 'session_extension_rejected',
      category: 'sessions',
      label: reason,
    });
  }

  trackSessionExtensionTimeout(): void {
    this.trackEvent({
      action: 'session_extension_timeout',
      category: 'sessions',
    });
  }

  trackSessionWindowWarning15min(): void {
    this.trackEvent({
      action: 'session_window_warning_15min_received',
      category: 'sessions',
    });
  }

  // ─── P2: Profile / GDPR events ─────────────────────────────────────────

  trackProfileUpdated(fields: string): void {
    this.trackEvent({
      action: 'profile_updated',
      category: 'profile',
      label: fields,
    });
  }

  trackLanguageChanged(language: string): void {
    this.trackEvent({
      action: 'language_changed',
      category: 'profile',
      label: language,
    });
  }

  trackGdprExportRequested(): void {
    this.trackEvent({
      action: 'gdpr_export_requested',
      category: 'profile',
    });
  }

  trackAccountSelfDeactivated(): void {
    this.trackEvent({
      action: 'account_self_deactivated',
      category: 'profile',
    });
  }

  // ─── P2: Telegram bot linking ──────────────────────────────────────────
  // No Telegram identity (chat_id, username) in these events — same
  // principle as the backend's telegram_linked/telegram_unlinked audit log.

  trackTelegramLinked(): void {
    this.trackEvent({
      action: 'telegram_linked',
      category: 'profile',
    });
  }

  trackTelegramUnlinked(): void {
    this.trackEvent({
      action: 'telegram_unlinked',
      category: 'profile',
    });
  }

  // ─── P2: WebSocket connectivity ────────────────────────────────────────

  trackWsConnected(): void {
    this.trackEvent({ action: 'ws_connected', category: 'connectivity' });
  }

  trackWsDisconnected(reason?: string): void {
    this.trackEvent({
      action: 'ws_disconnected',
      category: 'connectivity',
      label: reason,
    });
  }

  trackWsReconnect(attempt: number): void {
    this.trackEvent({
      action: 'ws_reconnect',
      category: 'connectivity',
      value: attempt,
    });
  }

  // ─── P2: Generic API error (called from axios interceptor) ─────────────

  trackApiError(statusCode: number, endpoint: string, errorCode?: string): void {
    this.trackEvent({
      action: 'api_error',
      category: 'errors',
      label: `${statusCode}:${endpoint}${errorCode ? `:${errorCode}` : ''}`,
      value: statusCode,
    });
  }

  // ─── Auto-release events ─────────────────────────────────────────────

  trackAutoReleaseToggled(enabled: boolean): void {
    this.trackEvent({
      action: enabled ? 'auto_release_enabled' : 'auto_release_disabled',
      category: 'auto_release',
    });
  }

  trackAutoReleaseRationaleShown(): void {
    this.trackEvent({ action: 'auto_release_rationale_shown', category: 'auto_release' });
  }

  trackLevelUpAcknowledged(level: number): void {
    this.trackEvent({ action: 'level_up_acknowledged', category: 'gamification', label: String(level) });
  }

  trackAutoReleaseRationaleAccepted(): void {
    this.trackEvent({ action: 'auto_release_rationale_accepted', category: 'auto_release' });
  }

  trackAutoReleaseRationaleDeclined(): void {
    this.trackEvent({ action: 'auto_release_rationale_declined', category: 'auto_release' });
  }

  trackAutoReleasePermission(state: string): void {
    this.trackEvent({
      action: 'auto_release_permission',
      category: 'auto_release',
      label: state,
    });
  }

  trackAutoReleaseDurationChanged(hours: number): void {
    this.trackEvent({
      action: 'auto_release_duration_changed',
      category: 'auto_release',
      value: hours,
    });
  }

  trackAutoReleaseWindowChanged(start: string, end: string): void {
    this.trackEvent({
      action: 'auto_release_window_changed',
      category: 'auto_release',
      label: `${start}-${end}`,
    });
  }

  trackAutoReleaseSubToggle(toggle: string, enabled: boolean): void {
    this.trackEvent({
      action: 'auto_release_sub_toggle',
      category: 'auto_release',
      label: `${toggle}:${enabled ? 'on' : 'off'}`,
    });
  }

  trackAutoReleasePauseToday(paused: boolean): void {
    this.trackEvent({
      action: paused ? 'auto_release_paused_today' : 'auto_release_resumed_today',
      category: 'auto_release',
    });
  }

  trackAutoReleaseConfirmReleased(): void {
    this.trackEvent({ action: 'auto_release_confirm_released', category: 'auto_release' });
  }

  trackAutoReleaseConfirmDismissed(): void {
    this.trackEvent({ action: 'auto_release_confirm_dismissed', category: 'auto_release' });
  }

  setAppVersion(version: string): void {
    if (typeof window === 'undefined' || !window.gtag) return;

    // Set GA4 user property for app version
    window.gtag('set', {
      'app_version': version,
    });

    console.log(`[GA4] App version set to: ${version}`);
  }

  // ─── P3.3 + P3.5: User properties ──────────────────────────────────────
  // Set GA4 user properties so reports can be sliced by building, role,
  // and language without overloading event `label` with high-cardinality
  // identifiers. Properties persist across the GA4 session.

  setUserProperties(props: {
    buildingId?: string;
    userRole?: 'owner' | 'occupant' | 'owner_occupant' | 'admin' | 'super_admin' | 'resident';
    language?: string;
  }): void {
    if (typeof window === 'undefined' || !window.gtag) return;

    const userProperties: Record<string, string> = {};
    if (props.buildingId !== undefined) userProperties.building_id = props.buildingId;
    if (props.userRole !== undefined) userProperties.user_role = props.userRole;
    if (props.language !== undefined) userProperties.language = props.language;

    if (Object.keys(userProperties).length === 0) return;

    // GA4 user-property API — these are scoped to the user, not the event
    window.gtag('set', 'user_properties', userProperties);
  }

  setUserId(userId: string | null): void {
    if (typeof window === 'undefined' || !window.gtag) return;
    // Setting user_id to null effectively clears it for the session
    window.gtag('set', { user_id: userId ?? undefined });
  }

  // ─── P3.4: Standardized timing events ──────────────────────────────────
  // GA4's `timing_complete` event aggregates in the standard timing reports.
  // Value is in milliseconds per GA convention.

  trackTiming(name: string, valueMs: number, category: string, label?: string): void {
    if (typeof window === 'undefined' || !window.gtag) return;
    window.gtag('event', 'timing_complete', {
      name,
      value: Math.round(valueMs),
      event_category: category,
      event_label: label,
    });
  }
}

// Create singleton instance
const analytics = new Analytics(
  import.meta.env.VITE_GA_MEASUREMENT_ID || 'G-GWY6G1M2EJ'
);

export default analytics;
